"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthToken } from "@/hooks/use-auth"

// API client configuration - empty string means same origin (Vite proxies /api to Express)
const API_BASE = ""

// ============================================
// Type Definitions
// ============================================

export interface Goal {
  id: string
  title: string
  description: string | null
  priority: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL"
  status: "DRAFT" | "IN_REVIEW" | "APPROVED" | "ARCHIVED"
  metrics: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export interface Persona {
  id: string
  name: string
  role: string | null
  demographics: Record<string, unknown> | null
  goals: string[] | null
  painPoints: string[] | null
  behaviors: string[] | null
  quotes: string[] | null
  imageUrl: string | null
  status: "DRAFT" | "IN_REVIEW" | "APPROVED" | "ARCHIVED"
  createdAt: string
  updatedAt: string
}

export interface Interview {
  id: string
  title: string
  participant: string | null
  date: string | null
  summary: string | null
  insights: string[] | null
  transcript: string | null
  status: "DRAFT" | "IN_REVIEW" | "APPROVED" | "ARCHIVED"
  createdAt: string
  updatedAt: string
}

export interface Competitor {
  id: string
  name: string
  website: string | null
  marketShare: string | null
  strengths: string[]
  weaknesses: string[]
  features: Array<{ feature: string; hasIt: boolean; notes?: string }> | null
  positioning: string | null
  createdAt: string
  updatedAt: string
}

export interface Journey {
  id: string
  title: string
  description: string | null
  stages: Array<{ name: string; touchpoints: string[]; painPoints?: string[]; opportunities?: string[] }>
  emotions: Record<string, unknown> | null
  opportunities: string[] | null
  status: "DRAFT" | "IN_REVIEW" | "APPROVED" | "ARCHIVED"
  createdAt: string
  updatedAt: string
}

export interface UseCase {
  id: string
  code: string
  name: string
  description: string
  preconditions: string[]
  mainFlow: Array<{ step: number; action: string }>
  alternativeFlows: Array<{ name: string; steps: string[] }> | null
  exceptionFlows: Array<{ name: string; trigger: string; steps: string[] }>
  postconditions: string[]
  businessRules: string[] | null
  status: "DRAFT" | "IN_REVIEW" | "APPROVED" | "ARCHIVED"
  createdAt: string
  updatedAt: string
}

export interface ServiceMap {
  id: string
  title: string
  description: string | null
  layers: Record<string, unknown>
  actors: Record<string, unknown> | null
  touchpoints: Record<string, unknown> | null
  status: "DRAFT" | "IN_REVIEW" | "APPROVED" | "ARCHIVED"
  createdAt: string
  updatedAt: string
}

export interface Storyboard {
  id: string
  title: string
  description: string | null
  frames: Array<{ id: string; title: string; description: string; imageUrl?: string }>
  narrative: string | null
  status: "DRAFT" | "IN_REVIEW" | "APPROVED" | "ARCHIVED"
  createdAt: string
  updatedAt: string
}

export interface Story {
  id: string
  title: string
  description: string | null
  acceptanceCriteria: string[] | null
  priority: number
  estimate: number | null
  status: "BACKLOG" | "READY" | "IN_PROGRESS" | "IN_REVIEW" | "DONE" | "BLOCKED"
  createdAt: string
  updatedAt: string
}

export interface Document {
  id: string
  filename: string
  mimeType: string
  size: number
  status: "PENDING" | "PROCESSING" | "PROCESSED" | "FAILED"
  chunksCount: number | null
  error: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
  uploadedBy: {
    id: string
    name: string | null
    email: string
  }
}

// ============================================
// API Response Types
// ============================================

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  meta: {
    pagination: {
      total: number
      page: number
      pageSize: number
      totalPages: number
    }
  }
}

// ============================================
// Generic fetch helpers
// ============================================

async function fetchArtifacts<T>(
  projectId: string,
  artifactType: string,
  token: string | null
): Promise<T[]> {
  if (!token) return []

  const response = await fetch(
    `${API_BASE}/api/projects/${projectId}/${artifactType}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    if (response.status === 401 || response.status === 404) {
      return []
    }
    throw new Error(`Failed to fetch ${artifactType}`)
  }

  const data: PaginatedResponse<T> | ApiResponse<T[]> = await response.json()
  
  if ('meta' in data) {
    return data.data
  }
  return data.data || []
}

// Helper for creating artifacts via supported endpoints (Definition API, documents, stories)
async function createArtifact<T, I>(
  projectId: string,
  artifactType: string,
  input: I,
  token: string | null
): Promise<T> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(
    `${API_BASE}/api/projects/${projectId}/${artifactType}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || `Failed to create ${artifactType}`)
  }

  const data: ApiResponse<T> = await response.json()
  return data.data
}

// Helper for deleting artifacts via supported endpoints
async function deleteArtifact(
  projectId: string,
  artifactType: string,
  id: string,
  token: string | null
): Promise<void> {
  if (!token) throw new Error("Authentication required")

  const response = await fetch(
    `${API_BASE}/api/projects/${projectId}/${artifactType}/${id}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || `Failed to delete ${artifactType}`)
  }
}

// ============================================
// Goals Hooks
// ============================================

export function useGoals(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "goals"],
    queryFn: () => fetchArtifacts<Goal>(projectId, "goals", token),
    enabled: !!projectId && !!token,
  })
}

// Note: Goal mutations removed - backend only supports GET for legacy artifacts.
// Use Definition API for full CRUD operations on personas, use-cases, journeys, etc.

// ============================================
// Personas Hooks
// ============================================

export function usePersonas(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "personas"],
    queryFn: () => fetchArtifacts<Persona>(projectId, "personas", token),
    enabled: !!projectId && !!token,
  })
}

// Note: Persona mutations removed - use Definition API (/api/projects/:id/definition/personas)

// ============================================
// Interviews Hooks
// ============================================

export function useInterviews(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "interviews"],
    queryFn: () => fetchArtifacts<Interview>(projectId, "interviews", token),
    enabled: !!projectId && !!token,
  })
}

// Note: Interview mutations removed - backend only supports GET for legacy artifacts

// ============================================
// Competitors Hooks
// ============================================

export function useCompetitors(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "competitors"],
    queryFn: () => fetchArtifacts<Competitor>(projectId, "competitors", token),
    enabled: !!projectId && !!token,
  })
}

// Note: Competitor mutations removed - backend only supports GET for legacy artifacts

// ============================================
// Journeys Hooks
// ============================================

export function useJourneys(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "journeys"],
    queryFn: () => fetchArtifacts<Journey>(projectId, "journeys", token),
    enabled: !!projectId && !!token,
  })
}

// Note: Journey mutations removed - use Definition API (/api/projects/:id/definition/journeys)

// ============================================
// Use Cases Hooks
// ============================================

export function useUseCases(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "definition", "use-cases"],
    queryFn: () => fetchArtifacts<UseCase>(projectId, "definition/use-cases", token),
    enabled: !!projectId && !!token,
  })
}

export function useCreateUseCase(projectId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()
  
  return useMutation({
    mutationFn: (input: Partial<UseCase>) =>
      createArtifact<UseCase, Partial<UseCase>>(projectId, "definition/use-cases", input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "definition", "use-cases"] })
    },
  })
}

// ============================================
// Service Maps Hooks
// ============================================

export function useServiceMaps(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "definition", "service-maps"],
    queryFn: () => fetchArtifacts<ServiceMap>(projectId, "definition/service-maps", token),
    enabled: !!projectId && !!token,
  })
}

// ============================================
// Storyboards Hooks
// ============================================

export function useStoryboards(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "definition", "storyboards"],
    queryFn: () => fetchArtifacts<Storyboard>(projectId, "definition/storyboards", token),
    enabled: !!projectId && !!token,
  })
}

// ============================================
// Stories Hooks
// ============================================

export function useStories(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "stories"],
    queryFn: () => fetchArtifacts<Story>(projectId, "stories", token),
    enabled: !!projectId && !!token,
  })
}

export function useCreateStory(projectId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()
  
  return useMutation({
    mutationFn: (input: Partial<Story>) =>
      createArtifact<Story, Partial<Story>>(projectId, "stories", input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "stories"] })
      queryClient.invalidateQueries({ queryKey: ["projects", projectId] })
    },
  })
}

// ============================================
// Documents Hooks
// ============================================

export function useDocuments(projectId: string) {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["projects", projectId, "documents"],
    queryFn: () => fetchArtifacts<Document>(projectId, "documents", token),
    enabled: !!projectId && !!token,
  })
}

export function useUploadDocument(projectId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()
  
  return useMutation({
    mutationFn: async (file: File) => {
      if (!token) throw new Error("Authentication required")
      
      const formData = new FormData()
      formData.append("file", file)
      
      const response = await fetch(
        `${API_BASE}/api/projects/${projectId}/documents`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      )
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }))
        throw new Error(error.message || "Failed to upload document")
      }
      
      const data: ApiResponse<Document> = await response.json()
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "documents"] })
    },
  })
}

export function useDeleteDocument(projectId: string) {
  const queryClient = useQueryClient()
  const token = useAuthToken()
  
  return useMutation({
    mutationFn: (id: string) => deleteArtifact(projectId, "documents", id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "documents"] })
    },
  })
}

export function useDownloadDocument(projectId: string) {
  const token = useAuthToken()
  
  return async (documentId: string, filename: string) => {
    if (!token) throw new Error("Authentication required")
    
    const response = await fetch(
      `${API_BASE}/api/projects/${projectId}/documents/${documentId}/download`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Download failed" }))
      throw new Error(error.error || "Failed to download document")
    }
    
    // Create blob and download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}

export function useViewDocument(projectId: string) {
  const token = useAuthToken()
  
  return async (documentId: string) => {
    if (!token) throw new Error("Authentication required")
    
    const response = await fetch(
      `${API_BASE}/api/projects/${projectId}/documents/${documentId}/download`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "View failed" }))
      throw new Error(error.error || "Failed to view document")
    }
    
    // Create blob and open in new tab
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    window.open(url, '_blank')
  }
}